/**
 * Audit Service
 * 
 * Comprehensive audit logging service for tracking all significant system actions.
 * Provides methods for logging events, retrieving audit logs, and managing retention.
 */

import { supabase } from './supabaseClient';
import type { AuditLog, AuditLogFilters, AuditLogCreateParams } from '../types/audit';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get current organization ID from user's profile
 */
async function getCurrentOrganisationId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profile } = await supabase
    .from('profiles')
    .select('organisation_id')
    .eq('id', user.id)
    .single();

  return profile?.organisation_id || null;
}

/**
 * Get current user information for audit logging
 */
async function getCurrentUserInfo(): Promise<{
  user_id: string | null;
  user_email: string | null;
  user_role: string | null;
}> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { user_id: null, user_email: null, user_role: null };
  }

  const { data: profile } = await supabase
    .from('profiles')
    .select('role, email')
    .eq('id', user.id)
    .single();

  return {
    user_id: user.id,
    user_email: profile?.email || user.email || null,
    user_role: profile?.role || null
  };
}

/**
 * Get client IP address (best effort)
 */
function getClientIpAddress(): string | null {
  // In browser environment, we can't reliably get the client IP
  // This would need to be passed from the server or edge function
  return null;
}

/**
 * Get user agent string
 */
function getUserAgent(): string | null {
  if (typeof navigator !== 'undefined') {
    return navigator.userAgent;
  }
  return null;
}

// ============================================================================
// AUDIT SERVICE
// ============================================================================

export const AuditService = {
  /**
   * Log an audit event
   */
  async log(params: AuditLogCreateParams): Promise<{ success: boolean; error?: string }> {
    try {
      const organisationId = await getCurrentOrganisationId();
      if (!organisationId) {
        console.warn('Cannot log audit event: No organisation ID');
        return { success: false, error: 'No organisation ID' };
      }

      const userInfo = await getCurrentUserInfo();

      const auditLog = {
        organisation_id: organisationId,
        user_id: userInfo.user_id,
        user_email: userInfo.user_email,
        user_role: userInfo.user_role,
        action: params.action,
        category: params.category,
        description: params.description,
        resource_type: params.resource_type || null,
        resource_id: params.resource_id || null,
        metadata: params.metadata || {},
        ip_address: params.ip_address || getClientIpAddress(),
        user_agent: params.user_agent || getUserAgent()
      };

      const { error } = await supabase
        .from('audit_logs')
        .insert(auditLog);

      if (error) {
        console.error('Failed to log audit event:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error logging audit event:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get audit logs with filtering and pagination
   */
  async getAuditLogs(
    filters: AuditLogFilters = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<{ data: AuditLog[]; error?: string }> {
    try {
      const organisationId = await getCurrentOrganisationId();
      if (!organisationId) {
        return { data: [], error: 'No organisation ID' };
      }

      let query = supabase
        .from('audit_logs')
        .select('*')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Apply filters
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.user_id) {
        query = query.eq('user_id', filters.user_id);
      }
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      if (filters.resource_type) {
        query = query.eq('resource_type', filters.resource_type);
      }
      if (filters.resource_id) {
        query = query.eq('resource_id', filters.resource_id);
      }
      if (filters.start_date) {
        query = query.gte('created_at', filters.start_date);
      }
      if (filters.end_date) {
        query = query.lte('created_at', filters.end_date);
      }
      if (filters.search) {
        query = query.or(`description.ilike.%${filters.search}%,action.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to get audit logs:', error);
        return { data: [], error: error.message };
      }

      return { data: data as AuditLog[] };
    } catch (error: any) {
      console.error('Error getting audit logs:', error);
      return { data: [], error: error.message };
    }
  },

  /**
   * Get total count of audit logs matching filters
   */
  async getAuditLogCount(filters: AuditLogFilters = {}): Promise<{ count: number; error?: string }> {
    try {
      const organisationId = await getCurrentOrganisationId();
      if (!organisationId) {
        return { count: 0, error: 'No organisation ID' };
      }

      let query = supabase
        .from('audit_logs')
        .select('*', { count: 'exact', head: true })
        .eq('organisation_id', organisationId);

      // Apply same filters as getAuditLogs
      if (filters.category) query = query.eq('category', filters.category);
      if (filters.user_id) query = query.eq('user_id', filters.user_id);
      if (filters.action) query = query.eq('action', filters.action);
      if (filters.resource_type) query = query.eq('resource_type', filters.resource_type);
      if (filters.resource_id) query = query.eq('resource_id', filters.resource_id);
      if (filters.start_date) query = query.gte('created_at', filters.start_date);
      if (filters.end_date) query = query.lte('created_at', filters.end_date);
      if (filters.search) {
        query = query.or(`description.ilike.%${filters.search}%,action.ilike.%${filters.search}%`);
      }

      const { count, error } = await query;

      if (error) {
        console.error('Failed to get audit log count:', error);
        return { count: 0, error: error.message };
      }

      return { count: count || 0 };
    } catch (error: any) {
      console.error('Error getting audit log count:', error);
      return { count: 0, error: error.message };
    }
  },

  /**
   * Get audit log statistics for the current organization
   */
  async getAuditLogStats(): Promise<{
    total_logs: number;
    logs_last_30_days: number;
    logs_last_90_days: number;
    oldest_log_date: string | null;
    estimated_size_mb: number;
  } | null> {
    try {
      const organisationId = await getCurrentOrganisationId();
      if (!organisationId) {
        return null;
      }

      const { data, error } = await supabase.rpc('get_audit_log_stats', {
        p_organisation_id: organisationId
      });

      if (error) {
        console.error('Failed to get audit log stats:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Error getting audit log stats:', error);
      return null;
    }
  },

  /**
   * Get retention settings for the current organization
   */
  async getRetentionSettings(): Promise<{
    retention_days: number;
    critical_categories: string[];
  } | null> {
    try {
      const organisationId = await getCurrentOrganisationId();
      if (!organisationId) {
        return null;
      }

      const { data, error } = await supabase
        .from('audit_log_retention_settings')
        .select('retention_days, critical_categories')
        .eq('organisation_id', organisationId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Failed to get retention settings:', error);
        return null;
      }

      // Return defaults if no settings exist
      return data || {
        retention_days: 90,
        critical_categories: ['billing', 'team', 'settings']
      };
    } catch (error) {
      console.error('Error getting retention settings:', error);
      return null;
    }
  },

  /**
   * Update retention settings for the current organization
   */
  async updateRetentionSettings(
    retentionDays: number,
    criticalCategories: string[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const organisationId = await getCurrentOrganisationId();
      if (!organisationId) {
        return { success: false, error: 'No organisation ID' };
      }

      const { error } = await supabase
        .from('audit_log_retention_settings')
        .upsert({
          organisation_id: organisationId,
          retention_days: retentionDays,
          critical_categories: criticalCategories,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Failed to update retention settings:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error updating retention settings:', error);
      return { success: false, error: error.message };
    }
  }
};

